import { createSelector } from '@reduxjs/toolkit';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import {
  MASK_FILTERS_ENDO_CODES,
  MASK_FILTERS_PERIO_CODES,
  MASK_FILTERS_PREVIOUS_TREATMENT,
  MASK_FILTERS_RESTORATIVE_CODES,
  Mask2DConfigCodes,
  MaskFilterParams,
  MaskGroup,
} from '@/shared/config';
import {
  filterConditionsWithLocalizations,
  getActiveConditionCodes,
  getGroupID,
  getMaxFaxLocalizations,
  getToothAnatomyLocalizations,
  processActiveConditionsMasks,
  processMaxFaxAnatomyLocalizations,
  processToothAnatomyLocalizations,
} from '@/shared/embeddedLibs/conditionsAndMasks';

import { assetsModel } from '@/entities/assets';
import {
  conditionModel,
  filterConditionsByReportID,
  filterPblConditions,
  getFDAVersion2MaskConditionCodes,
} from '@/entities/condition';
import { organizationModel } from '@/entities/organization';
import { reportsModel } from '@/entities/reports';
import { toothModel } from '@/entities/tooth';
import { toothLandmarksModel } from '@/entities/toothLandmarks';

import { RootState } from '@/app/model/store';

export const selectMasksFlags = createSelector(
  organizationModel.selectors.selectRelevantFeatureList,
  reportsModel.selectors.selectCurrentReportType,
  organizationModel.selectors.selectFDAEnvironment,
  (featureFlags, reportType, fdaEnvironment) => {
    const isGPReport = reportType === ReportType.ReportType_CBCT_GP;

    return {
      isCBCTMasksHidden:
        isGPReport &&
        featureFlags &&
        featureFlags[FeatureFlag.FeatureFlag_Hide_AllCBCTMasks],
      isConditionsMasksHidden:
        featureFlags &&
        featureFlags[FeatureFlag.FeatureFlag_Hide_ConditionsMasks],
      isAnatomyMasksHidden:
        featureFlags && featureFlags[FeatureFlag.FeatureFlag_Hide_AnatomyMasks],
      isLandmarksHidden:
        featureFlags &&
        featureFlags[FeatureFlag.FeatureFlag_Hide_PBLRulesAndMeasurements],
      isFDA: fdaEnvironment.isFDA,
      isFDAGPRestrictions: isGPReport && fdaEnvironment.isFDA3DGPRestrictions,
      isFDAVersion2: fdaEnvironment.isFDAVersion2,
      is2DReport: !isGPReport,
      masksVisible: fdaEnvironment.masksVisible,
    };
  },
);

export const selectFilters = createSelector(
  (state: RootState) => state.maskFilters.filters,
  selectMasksFlags,
  toothLandmarksModel.selectors.selectReportHasLandmarks,
  (filters, masksFlags) => {
    const {
      isCBCTMasksHidden,
      isConditionsMasksHidden,
      isAnatomyMasksHidden,
      isLandmarksHidden,
      isFDAGPRestrictions,
      isFDAVersion2,
      is2DReport,
    } = masksFlags;

    const fdaVersion2MaskGroups = isFDAVersion2
      ? getFDAVersion2MaskConditionCodes(is2DReport).map((conditionCode) =>
          getGroupID({ conditionCode }),
        )
      : undefined;

    const maskGroups = Object.entries(filters) as [
      MaskGroup,
      MaskFilterParams,
    ][];

    return maskGroups.map(([filterGroup, { disabled, isActive }]) => {
      // FDA 2.0 brings the Restorative and Perio buttons back (WA-6322, section 2)
      const isFDAHiddenGroup =
        isFDAGPRestrictions &&
        !isFDAVersion2 &&
        (filterGroup === 'perio' || filterGroup === 'restorative');
      const isFDAVersion2HiddenGroup =
        !!fdaVersion2MaskGroups && !fdaVersion2MaskGroups.includes(filterGroup);

      return [
        filterGroup,
        {
          disabled,
          isActive,
          hide:
            !!isCBCTMasksHidden ||
            !!isFDAHiddenGroup ||
            isFDAVersion2HiddenGroup ||
            (!!isConditionsMasksHidden && filterGroup !== 'chart') ||
            (!!isAnatomyMasksHidden && filterGroup === 'chart') ||
            (!!isLandmarksHidden && filterGroup === 'perio'),
        },
      ];
    }) as [MaskGroup, MaskFilterParams][];
  },
);

export const selectMaxFaxLocalizations = createSelector(
  reportsModel.selectors.selectCurrentReport,
  getMaxFaxLocalizations,
);

export const selectMaskActiveConditionCodes = createSelector(
  selectFilters,
  getActiveConditionCodes,
);

export const selectConditionsWithLocalizations = createSelector(
  conditionModel.selectors.selectAll,
  reportsModel.selectors.selectCurrentReport,
  assetsModel.selectors.selectAll,
  (conditions, report, assets) => {
    if (!report || !assets) {
      return [];
    }

    return filterConditionsWithLocalizations(conditions, report, assets);
  },
);

export const selectConditionCodesWithMasks = createSelector(
  selectConditionsWithLocalizations,
  (conditions) => conditions.map((condition) => condition.Code),
);

export const selectHasMasksList = createSelector(
  selectConditionCodesWithMasks,
  selectMasksFlags,
  (conditionCodes, masksFlags) => {
    const { isFDAVersion2, is2DReport } = masksFlags;

    const fdaVersion2MaskCodes = isFDAVersion2
      ? getFDAVersion2MaskConditionCodes(is2DReport)
      : undefined;

    const maskConditionCodes = fdaVersion2MaskCodes
      ? conditionCodes.filter((code) => fdaVersion2MaskCodes.includes(code))
      : conditionCodes;

    const result = maskConditionCodes.reduce(
      (acc, code) => ({
        hasPerioMasks:
          acc.hasPerioMasks || MASK_FILTERS_PERIO_CODES.includes(code),
        hasEndoMasks:
          acc.hasEndoMasks || MASK_FILTERS_ENDO_CODES.includes(code),
        hasRestorativeMasks:
          acc.hasRestorativeMasks ||
          MASK_FILTERS_RESTORATIVE_CODES.includes(code),
        hasChartMasks:
          acc.hasChartMasks || MASK_FILTERS_PREVIOUS_TREATMENT.includes(code),
      }),
      {
        hasPerioMasks: false,
        hasEndoMasks: false,
        hasRestorativeMasks: false,
        hasChartMasks: false,
      },
    );

    return result;
  },
);

export const selectActiveConditionsMasks = createSelector(
  selectConditionsWithLocalizations,
  selectMaskActiveConditionCodes,
  processActiveConditionsMasks,
);

export const selectToothAnatomies = createSelector(
  reportsModel.selectors.selectCurrentReportID,
  toothModel.selectors.selectAll,
  getToothAnatomyLocalizations,
);

export const selectMaxFaxAnatomy2DMaskData = createSelector(
  selectMaxFaxLocalizations,
  processMaxFaxAnatomyLocalizations,
);

export const selectToothAnatomy2DMaskData = createSelector(
  selectToothAnatomies,
  processToothAnatomyLocalizations,
);

export const selectReportActiveMasks = createSelector(
  selectActiveConditionsMasks,
  selectToothAnatomy2DMaskData,
  selectMaxFaxAnatomy2DMaskData,
  selectMasksFlags,
  (
    masks2DConfig,
    toothAnatomyMaskData,
    maxFaxAnatomy2DMaskData,
    masksFlags,
  ) => {
    const {
      isConditionsMasksHidden,
      isAnatomyMasksHidden,
      isCBCTMasksHidden,
      isFDAVersion2,
      is2DReport,
      masksVisible,
    } = masksFlags;

    // Hide all masks when masksVisible is false (FDA unaided mode or loading state)
    if (!masksVisible) {
      return [];
    }

    const fdaVersion2AllowedMaskCodes = isFDAVersion2
      ? getFDAVersion2MaskConditionCodes(is2DReport)
      : undefined;

    const filteredConditionsMasksByAnatomyFlag = isAnatomyMasksHidden
      ? masks2DConfig.filter((mask) => mask.groupID !== 'chart')
      : masks2DConfig;

    const filteredConditionsMasksByFDA2 = fdaVersion2AllowedMaskCodes
      ? filteredConditionsMasksByAnatomyFlag.filter((mask) => {
          const conditionCode = (mask.meta?.codes as Mask2DConfigCodes)
            ?.conditionCode;

          return (
            conditionCode !== undefined &&
            fdaVersion2AllowedMaskCodes.includes(conditionCode)
          );
        })
      : filteredConditionsMasksByAnatomyFlag;

    const filteredConditionsMasksData =
      isConditionsMasksHidden || isCBCTMasksHidden
        ? []
        : [...filteredConditionsMasksByFDA2];

    const filteredToothAnatomyMaskData =
      isAnatomyMasksHidden || isCBCTMasksHidden || isFDAVersion2
        ? []
        : [...toothAnatomyMaskData];

    const filteredMaxFaxAnatomy2DMaskData =
      isAnatomyMasksHidden || isCBCTMasksHidden || isFDAVersion2
        ? []
        : [...maxFaxAnatomy2DMaskData];

    const allMasks = [
      ...filteredConditionsMasksData,
      ...filteredToothAnatomyMaskData,
      ...filteredMaxFaxAnatomy2DMaskData,
    ];

    return allMasks;
  },
);

export const selectHasPBLConditions = createSelector(
  reportsModel.selectors.selectCurrentReportID,
  conditionModel.selectors.selectAll,
  (reportID, conditions) =>
    filterPblConditions(filterConditionsByReportID(conditions, reportID ?? ''))
      .length > 0,
);

export const selectMaskFilterDisabled = (maskFilterType: MaskGroup) =>
  createSelector(
    selectHasMasksList,
    selectHasPBLConditions,
    toothLandmarksModel.selectors.selectReportHasLandmarks,
    reportsModel.selectors.selectCurrentReportType,
    selectMasksFlags,
    (
      hasMasksList,
      hasPBLConditions,
      isReportHasLandmarks,
      reportType,
      masksFlags,
    ) => {
      switch (maskFilterType) {
        case 'restorative': {
          return !hasMasksList.hasRestorativeMasks;
        }
        case 'endo': {
          return !hasMasksList.hasEndoMasks;
        }
        case 'perio': {
          if (masksFlags.isFDAVersion2) {
            return !hasMasksList.hasPerioMasks;
          }

          const isCBCTReport = reportType === ReportType.ReportType_CBCT_GP;
          return (
            !hasMasksList.hasPerioMasks &&
            !hasPBLConditions &&
            (isCBCTReport || !isReportHasLandmarks)
          );
        }
        case 'chart': {
          return masksFlags.isFDAVersion2 && !hasMasksList.hasChartMasks;
        }
        default: {
          return false;
        }
      }
    },
  );

export const selectActiveFilters = createSelector(selectFilters, (allFilters) =>
  allFilters
    .reduce(
      (filtersAccumulator, [filterGroup, { isActive }]) =>
        isActive ? filtersAccumulator.concat(filterGroup) : filtersAccumulator,
      [] as MaskGroup[],
    )
    .sort(),
);

export const selectFiltersIDs = (state: RootState) =>
  state.maskFilters.filterIDs;

export const selectIsLandmarksShown = (state: RootState) =>
  state.maskFilters.isLandmarksShown;
